// src/components/chat/SentRequestWindow.jsx - RESPONSIVE VERSION
import React from 'react';
import styles from './SentRequestWindow.module.css';
import { formatLocalDate, formatLocalTime } from '../../utils/dateTime';
import { useNavigate } from 'react-router-dom';

export default function SentRequestWindow({
  selectedUser,
  isMobileVisible = false,   // ← NEW
  onMobileBack,              // ← NEW
}) {
  const navigate = useNavigate();

  const handleViewProfile = () => {
    if (!selectedUser?.id) return;
    navigate(`/profile/${selectedUser.id}`);
  };

  const getStatusInfo = () => {
    if (!selectedUser?.status && selectedUser?.status !== 0) {
      return { text: 'Pending', color: '#f59e0b', icon: '⏳' };
    }
    switch (selectedUser.status) {
      case 0: return { text: 'Pending',  color: '#f59e0b', icon: '⏳' };
      case 1: return { text: 'Accepted', color: '#10b981', icon: '✓' };
      case 2: return { text: 'Declined', color: '#ef4444', icon: '✕' };
      default: return { text: 'Unknown', color: '#6b7280', icon: '?' };
    }
  };

  const statusInfo = getStatusInfo();

  /* ─── Empty state ─── */
  if (!selectedUser) {
    return (
      <div className={[styles.sentRequestWindow, isMobileVisible ? styles.panelVisible : ''].filter(Boolean).join(' ')}>
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📤</div>
          <h3>Select a sent request</h3>
          <p>Choose a request from the sidebar to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className={[styles.sentRequestWindow, isMobileVisible ? styles.panelVisible : ''].filter(Boolean).join(' ')}>

      {/* Header */}
      <div className={styles.header}>
        {/* ← Back button (mobile only) */}
        <button className={styles.backBtn} onClick={onMobileBack} aria-label="Back">‹</button>

        <div className={styles.userInfo}>
          {selectedUser.profilePhotoUrl ? (
            <img
              src={selectedUser.profilePhotoUrl}
              alt={selectedUser.name}
              className={styles.avatar}
              onClick={handleViewProfile}
              style={{ cursor: 'pointer' }}
              title="View profile"
            />
          ) : (
            <div className={styles.avatarPlaceholder} onClick={handleViewProfile} title="View profile">
              {selectedUser.name?.charAt(0).toUpperCase() || '?'}
            </div>
          )}
          <div>
            <h2 className={styles.userName} onClick={handleViewProfile} title="View profile">
              {selectedUser.name}
            </h2>
            <p className={styles.requestTime}>Sent {formatLocalDate(selectedUser.lastSentAt)}</p>
          </div>
        </div>

        {/* Status Badge */}
        <div className={styles.statusBadge} style={{ backgroundColor: statusInfo.color }}>
          <span className={styles.statusIcon}>{statusInfo.icon}</span>
          <span className={styles.statusText}>{statusInfo.text}</span>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.requestCard}>
          <div className={styles.requestHeader}>
            <span className={styles.requestIcon}>📨</span>
            <h3>Chat Request Sent</h3>
          </div>

          <div className={styles.requestBody}>
            <p className={styles.requestMessage}>
              {selectedUser.lastMessage || 'You sent a connection request'}
            </p>
            {selectedUser.lastSentAt && (
              <p className={styles.requestMeta}>
                <span className={styles.timeIcon}>🕐</span>
                {formatLocalTime(selectedUser.lastSentAt)}
              </p>
            )}
          </div>

          {/* Status Details */}
          <div className={styles.statusSection}>
            {selectedUser.status === 0 && (
              <div className={styles.infoItem}>
                <span className={styles.infoIcon}>⏳</span>
                <p>Waiting for {selectedUser.name} to respond to your request.</p>
              </div>
            )}
            {selectedUser.status === 1 && (
              <div className={styles.infoItem}>
                <span className={styles.infoIcon}>✓</span>
                <p>
                  {selectedUser.name} accepted your request
                  {selectedUser.respondedAt && ` on ${formatLocalDate(selectedUser.respondedAt)}`}.
                  You can now message each other!
                </p>
              </div>
            )}
            {selectedUser.status === 2 && (
              <div className={styles.infoItem}>
                <span className={styles.infoIcon}>✕</span>
                <p>
                  {selectedUser.name} declined your request
                  {selectedUser.respondedAt && ` on ${formatLocalDate(selectedUser.respondedAt)}`}.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.viewProfileBtn} onClick={handleViewProfile}>
            <span className={styles.btnIcon}>👤</span>View Profile
          </button>

          {selectedUser.status === 1 && (
            <button
              className={styles.messageBtnAccepted}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('selectChat', { detail: { userId: selectedUser.id } }));
              }}
            >
              <span className={styles.btnIcon}>💬</span>Open Chat
            </button>
          )}
        </div>

        <div className={styles.footer}>
          {selectedUser.status === 0 && (
            <p className={styles.footerText}>
              💡 <strong>Tip:</strong> Once accepted, you'll be able to send messages.
            </p>
          )}
          {selectedUser.status === 1 && (
            <p className={styles.footerText}>🎉 <strong>Great!</strong> Your connection request was accepted.</p>
          )}
          {selectedUser.status === 2 && (
            <p className={styles.footerText}>ℹ️ You can try connecting with other users.</p>
          )}
        </div>
      </div>
    </div>
  );
}